import React from 'react';

/**
 * EGGS Icon Component
 * Based on Feather Icons from EGGS Design System
 * 
 * @param {string} name - Icon name (e.g., 'arrow-right', 'user', 'settings')
 * @param {number} size - Icon size in pixels (default: 24)
 * @param {string} color - Icon color (default: inherit)
 * @param {string} className - Additional CSS classes
 * @param {object} props - Additional SVG props
 */
export default function Icon({
  name,
  size = 24,
  color = 'currentColor',
  className = '',
  ...props
}) {
  // Icon definitions - Basic set for immediate use
  const icons = {
    'arrow-right': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12"></line>
        <polyline points="12,5 19,12 12,19"></polyline>
      </svg>
    ),
    'arrow-left': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12"></line>
        <polyline points="12,19 5,12 12,5"></polyline>
      </svg>
    ),
    'arrow-up': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="19" x2="12" y2="5"></line>
        <polyline points="5,12 12,5 19,12"></polyline>
      </svg>
    ),
    'arrow-down': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <polyline points="19,12 12,19 5,12"></polyline>
      </svg>
    ),
    'chevron-right': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9,18 15,12 9,6"></polyline>
      </svg>
    ),
    'chevron-left': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15,18 9,12 15,6"></polyline>
      </svg>
    ),
    'chevron-up': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="18,15 12,9 6,15"></polyline>
      </svg>
    ),
    'chevron-down': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6,9 12,15 18,9"></polyline>
      </svg>
    ),
    'user': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    ),
    'users': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    ),
    'settings': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"></circle>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
      </svg>
    ),
    'menu': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="6" x2="21" y2="6"></line>
        <line x1="3" y1="12" x2="21" y2="12"></line>
        <line x1="3" y1="18" x2="21" y2="18"></line>
      </svg>
    ),
    'x': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    ),
    'search': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <path d="M21 21l-4.35-4.35"></path>
      </svg>
    ),
    'bell': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
      </svg>
    ),
    'message-circle': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
      </svg>
    ),
    'calendar': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
      </svg>
    ),
    'home': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9,22 9,12 15,12 15,22"></polyline>
      </svg>
    ),
    'star': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
      </svg>
    ),
    'check': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20,6 9,17 4,12"></polyline>
      </svg>
    ),
    'check-circle': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22,4 12,14.01 9,11.01"></polyline>
      </svg>
    ),
    'alert-circle': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
    ),
    'x-circle': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
    ),
    'lock': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
      </svg>
    ),
    'map-pin': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
        <circle cx="12" cy="10" r="3"></circle>
      </svg>
    ),
    'link': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
      </svg>
    ),
    'bar-chart': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="20" x2="12" y2="10"></line>
        <line x1="18" y1="20" x2="18" y2="4"></line>
        <line x1="6" y1="20" x2="6" y2="16"></line>
      </svg>
    ),
    'file-text': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14,2 14,8 20,8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10,9 9,9 8,9"></polyline>
      </svg>
    ),
    'target': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <circle cx="12" cy="12" r="6"></circle>
        <circle cx="12" cy="12" r="2"></circle>
      </svg>
    ),
    'scan': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 7V5a2 2 0 0 1 2-2h2"></path>
        <path d="M17 3h2a2 2 0 0 1 2 2v2"></path>
        <path d="M21 17v2a2 2 0 0 1-2 2h-2"></path>
        <path d="M7 21H5a2 2 0 0 1-2-2v-2"></path>
        <line x1="7" y1="12" x2="17" y2="12"></line>
      </svg>
    ),
    'ticket': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path>
        <path d="M13 5v2"></path>
        <path d="M13 17v2"></path>
        <path d="M13 11v2"></path>
      </svg>
    ),
    'info': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="16" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12.01" y2="8"></line>
      </svg>
    ),
    'plus': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
    ),
    'edit': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
      </svg>
    ),
    'trash-2': (
      <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3,6 5,6 21,6"></polyline>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        <line x1="10" y1="11" x2="10" y2="17"></line>
        <line x1="14" y1="11" x2="14" y2="17"></line>
      </svg>
    ),
    'award': (
      <svg viewBox="0 0 16 24" fill={color} stroke="none">
        <path d="M14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14C9.17266 14 10.2662 13.6626 11.1904 13.0811C11.2298 13.0515 11.2707 13.0237 11.3145 13C12.9324 11.9253 14 10.088 14 8ZM10.9854 15.4209C10.0626 15.7925 9.05577 16 8 16C6.94436 16 5.93734 15.7934 5.01465 15.4219L4.26367 21.0742L7.48535 19.1426L7.60742 19.0801C7.89967 18.9553 8.2375 18.9763 8.51465 19.1426L11.7354 21.0752L10.9854 15.4209ZM16 8C16 10.5891 14.7688 12.8894 12.8613 14.3516L13.9912 22.8682C14.0417 23.2488 13.8698 23.6251 13.5488 23.8359C13.2279 24.0466 12.8146 24.0549 12.4854 23.8574L8 21.166L3.51465 23.8574C3.18545 24.0549 2.77208 24.0466 2.45117 23.8359C2.13023 23.6251 1.95823 23.2488 2.00879 22.8682L3.13965 14.3525C1.23152 12.8904 0 10.5896 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8Z" />
      </svg>
    ),
    'shield': (
      <svg viewBox="0 0 24 24" fill={color} stroke="none">
        <path d="M12.4473 22.8945C12.1657 23.0353 11.8343 23.0353 11.5527 22.8945L12 22L12.4473 22.8945ZM19 5.69336L12 3.06836L5 5.69336V12C5 14.5534 6.71809 16.7994 8.6582 18.4971C9.60732 19.3275 10.5617 19.9845 11.2803 20.4336C11.5629 20.6102 11.8086 20.7518 12 20.8604C12.1914 20.7518 12.4371 20.6102 12.7197 20.4336C13.4383 19.9845 14.3927 19.3275 15.3418 18.4971C17.2819 16.7994 19 14.5534 19 12V5.69336ZM21 12C21 15.4464 18.718 18.2006 16.6582 20.0029C15.6076 20.9222 14.5616 21.6406 13.7803 22.1289C13.3885 22.3738 13.0604 22.5628 12.8281 22.6914C12.712 22.7557 12.6194 22.8049 12.5547 22.8389C12.5223 22.8559 12.4966 22.8697 12.4785 22.8789L12.4512 22.8926L12.4482 22.8936V22.8945C12.4479 22.8947 12.4472 22.8944 12 22C11.5528 22.8944 11.5521 22.8947 11.5518 22.8945V22.8936L11.5488 22.8926L11.5215 22.8789C11.5034 22.8697 11.4777 22.8559 11.4453 22.8389C11.3806 22.8049 11.288 22.7557 11.1719 22.6914C10.9396 22.5628 10.6115 22.3738 10.2197 22.1289C9.43839 21.6406 8.39241 20.9222 7.3418 20.0029C5.28205 18.2006 3 15.4464 3 12V5C3 4.58327 3.25831 4.20992 3.64844 4.06348L11.6484 1.06348L11.7344 1.03613C11.937 0.980286 12.1533 0.98914 12.3516 1.06348L20.3516 4.06348C20.7417 4.20992 21 4.58327 21 5V12Z" />
      </svg>
    ),
    'microsoft': (
      <svg viewBox="0 0 23 23" fill="none">
        <path d="M0 0h11v11H0z" fill="#F25022"/>
        <path d="M12 0h11v11H12z" fill="#7FBA00"/>
        <path d="M0 12h11v11H0z" fill="#00A4EF"/>
        <path d="M12 12h11v11H12z" fill="#FFB900"/>
      </svg>
    ),
    'linkedin': (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    'apple': (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
      </svg>
    ),
    'google': (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
    ),
    'play': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M4.52051 2.12208C4.84158 1.94679 5.23331 1.96137 5.54102 2.15919L19.541 11.1592C19.827 11.3432 20 11.6599 20 12C20 12.3401 19.827 12.6568 19.541 12.8408L5.54102 21.8408C5.23331 22.0386 4.84158 22.0532 4.52051 21.8779C4.19952 21.7026 4 21.3658 4 21V3.00001C4 2.63425 4.19952 2.29739 4.52051 2.12208ZM6 19.168L17.1504 12L6 4.83106V19.168Z"/>
      </svg>
    ),
    'play-circle': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12ZM9.52832 7.11816C9.85344 6.94426 10.2479 6.96344 10.5547 7.16797L16.5547 11.168C16.8329 11.3534 17 11.6656 17 12C17 12.3344 16.8329 12.6466 16.5547 12.832L10.5547 16.832C10.2479 17.0366 9.85344 17.0557 9.52832 16.8818C9.20316 16.7078 9 16.3688 9 16V8C9 7.6312 9.20316 7.29218 9.52832 7.11816ZM11 14.1309L14.1973 12L11 9.86816V14.1309ZM23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12Z"/>
      </svg>
    ),
    'volume-x': (
      <svg viewBox="0 0 23 16" fill="currentColor" stroke="none">
        <path d="M9.375 0.218934C9.67517 -0.021202 10.0871 -0.0677031 10.4336 0.0988169C10.7799 0.265396 11 0.615891 11 1.00018V15.0002C11 15.3845 10.7799 15.735 10.4336 15.9016C10.0871 16.0681 9.67517 16.0216 9.375 15.7814L4.64844 12.0002H1C0.447715 12.0002 0 11.5525 0 11.0002V5.00018C0 4.4479 0.447715 4.00018 1 4.00018H4.64844L9.375 0.218934ZM5.625 5.78143C5.44769 5.92328 5.22707 6.00018 5 6.00018H2V10.0002H5C5.22707 10.0002 5.44769 10.0771 5.625 10.2189L9 12.9182V3.08124L5.625 5.78143ZM21.293 4.29315C21.6835 3.90263 22.3165 3.90263 22.707 4.29315C23.0976 4.68368 23.0976 5.31669 22.707 5.70722L20.4141 8.00018L22.707 10.2932C23.0976 10.6837 23.0976 11.3167 22.707 11.7072C22.3165 12.0977 21.6835 12.0977 21.293 11.7072L19 9.41425L16.707 11.7072C16.3165 12.0977 15.6835 12.0977 15.293 11.7072C14.9024 11.3167 14.9024 10.6837 15.293 10.2932L17.5859 8.00018L15.293 5.70722C14.9024 5.31669 14.9024 4.68368 15.293 4.29315C15.6835 3.90263 16.3165 3.90263 16.707 4.29315L19 6.58612L21.293 4.29315Z"/>
      </svg>
    ),
    'volume-2': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M20.998 12.0003C20.998 9.61383 20.0495 7.32478 18.3623 5.63703C17.9721 5.24643 17.9728 4.61336 18.3633 4.22297C18.7539 3.83255 19.3869 3.8324 19.7773 4.22297C21.8395 6.28577 22.998 9.08353 22.998 12.0003C22.998 14.9169 21.8393 17.714 19.7773 19.7767C19.387 20.1671 18.7539 20.1678 18.3633 19.7777C17.9727 19.3872 17.9719 18.7532 18.3623 18.3626C20.0495 16.6749 20.998 14.3867 20.998 12.0003ZM10.375 4.21906C10.6752 3.97893 11.0871 3.93242 11.4336 4.09894C11.7798 4.26555 12 4.61606 12 5.00031V19.0003C11.9999 19.3845 11.7799 19.7352 11.4336 19.9017C11.0871 20.0682 10.6752 20.0207 10.375 19.7806L5.64941 16.0003H2C1.44782 16.0003 1.00017 15.5525 1 15.0003V9.00031C1 8.44803 1.44772 8.00031 2 8.00031H5.64844L10.375 4.21906ZM6.625 9.78059C6.44769 9.92244 6.22707 10.0003 6 10.0003H3V14.0003H6L6.16895 14.014C6.33486 14.0424 6.49213 14.1128 6.625 14.2191L10 16.9183V7.08039L6.625 9.78059ZM16.0039 11.9954C16.0039 10.9349 15.5827 9.91739 14.833 9.1673C14.4426 8.7768 14.4426 8.14372 14.833 7.75324C15.2236 7.36278 15.8566 7.36266 16.2471 7.75324C17.3719 8.8784 18.0039 10.4045 18.0039 11.9954C18.0038 13.5862 17.3717 15.1116 16.2471 16.2366C15.8567 16.6272 15.2236 16.6279 14.833 16.2376C14.4424 15.8472 14.4425 15.2132 14.833 14.8226C15.5826 14.0726 16.0038 13.0558 16.0039 11.9954Z"/>
      </svg>
    ),
    'volume-1': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M10.375 4.21875C10.6752 3.97861 11.0871 3.93211 11.4336 4.09863C11.7799 4.26521 12 4.61571 12 5V19C12 19.3843 11.7799 19.7348 11.4336 19.9014C11.0871 20.0679 10.6752 20.0214 10.375 19.7813L5.64844 16H2C1.44772 16 1 15.5523 1 15V9C1 8.44772 1.44772 8 2 8H5.64844L10.375 4.21875ZM6.625 9.78125C6.44769 9.9231 6.22707 10 6 10H3V14H6C6.22707 14 6.44769 14.0769 6.625 14.2188L10 16.918V7.08106L6.625 9.78125ZM16.0039 11.9951C16.0039 10.9345 15.5828 9.91709 14.833 9.16699C14.4426 8.77643 14.4425 8.1434 14.833 7.75293C15.2236 7.3625 15.8566 7.36245 16.2471 7.75293C17.3719 8.8781 18.0039 10.4041 18.0039 11.9951C18.0039 13.5861 17.3719 15.1122 16.2471 16.2373C15.8566 16.6276 15.2235 16.6276 14.833 16.2373C14.4424 15.8468 14.4426 15.2138 14.833 14.8232C15.5829 14.0732 16.0039 13.0557 16.0039 11.9951Z"/>
      </svg>
    ),
    'volume': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M10.375 4.21875C10.6752 3.97861 11.0871 3.93211 11.4336 4.09863C11.7799 4.26521 12 4.61571 12 5V19C12 19.3843 11.7799 19.7348 11.4336 19.9014C11.0871 20.0679 10.6752 20.0214 10.375 19.7813L5.64844 16H2C1.44772 16 1 15.5523 1 15V9C1 8.44772 1.44772 8 2 8H5.64844L10.375 4.21875ZM6.625 9.78125C6.44769 9.9231 6.22707 10 6 10H3V14H6C6.22707 14 6.44769 14.0769 6.625 14.2188L10 16.918V7.08106L6.625 9.78125Z"/>
      </svg>
    ),
    'skip-forward': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M4.56641 3.09863C4.91287 2.93211 5.32483 2.97861 5.625 3.21875L15.625 11.2188C15.8622 11.4085 16 11.6962 16 12C16 12.3038 15.8622 12.5915 15.625 12.7813L5.625 20.7812C5.32483 21.0214 4.91287 21.0679 4.56641 20.9014C4.22012 20.7348 4 20.3843 4 20V4C4 3.61571 4.22012 3.26521 4.56641 3.09863ZM18 19V5C18 4.44772 18.4477 4 19 4C19.5523 4 20 4.44772 20 5V19C20 19.5523 19.5523 20 19 20C18.4477 20 18 19.5523 18 19ZM6 17.918L13.3984 12L6 6.08106V17.918Z"/>
      </svg>
    ),
    'skip-back': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M18.375 3.21875C18.6752 2.97861 19.0871 2.93211 19.4336 3.09863C19.7799 3.26521 20 3.61571 20 4V20C20 20.3843 19.7799 20.7348 19.4336 20.9014C19.0871 21.0679 18.6752 21.0214 18.375 20.7812L8.375 12.7813C8.13778 12.5915 8 12.3038 8 12C8 11.6962 8.13778 11.4085 8.375 11.2188L18.375 3.21875ZM4 19V5C4 4.44772 4.44772 4 5 4C5.55228 4 6 4.44772 6 5V19C6 19.5523 5.55228 20 5 20C4.44772 20 4 19.5523 4 19ZM10.6016 12L18 17.918V6.08106L10.6016 12Z"/>
      </svg>
    ),
    'rewind': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M10.3857 4.21093C10.6872 3.97645 11.0964 3.93376 11.4395 4.10156C11.7825 4.26937 12 4.61808 12 5V19C12 19.3819 11.7825 19.7306 11.4395 19.8984C11.0964 20.0662 10.6872 20.0235 10.3857 19.7891L1.38574 12.7891C1.14236 12.5996 1 12.3084 1 12C1 11.6915 1.14236 11.4004 1.38574 11.2109L10.3857 4.21093ZM21.3857 4.21093C21.6872 3.97645 22.0964 3.93376 22.4395 4.10156C22.7825 4.26937 23 4.61808 23 5V19C23 19.3819 22.7825 19.7306 22.4395 19.8984C22.0964 20.0662 21.6872 20.0235 21.3857 19.7891L12.3857 12.7891C12.1424 12.5996 12 12.3084 12 12C12 11.6915 12.1424 11.4004 12.3857 11.2109L21.3857 4.21093ZM3.62793 12L10 16.9551V7.04394L3.62793 12ZM14.6279 12L21 16.9551V7.04394L14.6279 12Z"/>
      </svg>
    ),
    'fast-forward': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M1.56055 4.10157C1.90364 3.93377 2.31278 3.97646 2.61426 4.21095L11.6143 11.2109C11.8576 11.4004 12 11.6916 12 12C12 12.3085 11.8576 12.5996 11.6143 12.7891L2.61426 19.7891C2.31278 20.0236 1.90364 20.0663 1.56055 19.8984C1.21747 19.7306 1 19.3819 1 19V5.00001C1 4.61809 1.21747 4.26938 1.56055 4.10157ZM12.5605 4.10157C12.9036 3.93377 13.3128 3.97646 13.6143 4.21095L22.6143 11.2109C22.8576 11.4004 23 11.6916 23 12C23 12.3085 22.8576 12.5996 22.6143 12.7891L13.6143 19.7891C13.3128 20.0236 12.9036 20.0663 12.5605 19.8984C12.2175 19.7306 12 19.3819 12 19V5.00001C12 4.61809 12.2175 4.26938 12.5605 4.10157ZM3 16.9551L9.37109 12L3 7.04396V16.9551ZM14 16.9551L20.3711 12L14 7.04396V16.9551Z"/>
      </svg>
    ),
    'pause': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M10 3C10.5523 3 11 3.44772 11 4V20C11 20.5523 10.5523 21 10 21H6C5.44772 21 5 20.5523 5 20V4C5 3.44772 5.44772 3 6 3H10ZM18 3C18.5523 3 19 3.44772 19 4V20C19 20.5523 18.5523 21 18 21H14C13.4477 21 13 20.5523 13 20V4C13 3.44772 13.4477 3 14 3H18ZM15 19H17V5H15V19ZM7 19H9V5H7V19Z"/>
      </svg>
    ),
    'pause-circle': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12ZM9 15V9C9 8.44772 9.44771 8 10 8C10.5523 8 11 8.44772 11 9V15C11 15.5523 10.5523 16 10 16C9.44771 16 9 15.5523 9 15ZM13 15V9C13 8.44772 13.4477 8 14 8C14.5523 8 15 8.44772 15 9V15C15 15.5523 14.5523 16 14 16C13.4477 16 13 15.5523 13 15ZM23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12Z"/>
      </svg>
    ),
    'bolt': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M12.2314 1.35937C12.5156 1.01841 12.9912 0.906248 13.3984 1.083C13.8054 1.25985 14.0472 1.68363 13.9922 2.12402L13.1328 8.99999H21C21.388 8.99999 21.7407 9.22478 21.9053 9.57617C22.0698 9.92752 22.0169 10.3426 21.7685 10.6406L11.7685 22.6406C11.4843 22.9816 11.0087 23.0937 10.6016 22.917C10.1945 22.7401 9.95275 22.3164 10.0078 21.876L10.8672 15H2.99999C2.61197 15 2.25929 14.7752 2.09471 14.4238C1.93022 14.0725 1.98306 13.6574 2.23143 13.3594L12.2314 1.35937Z"/>
      </svg>
    ),
    'help-circle': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
    'maximize': (
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M2 19V16C2 15.4477 2.44772 15 3 15C3.55228 15 4 15.4477 4 16V19C4 19.2652 4.10543 19.5195 4.29297 19.707C4.48051 19.8946 4.73478 20 5 20H8C8.55228 20 9 20.4477 9 21C9 21.5523 8.55228 22 8 22H5C4.20435 22 3.44151 21.6837 2.87891 21.1211C2.3163 20.5585 2 19.7957 2 19ZM20 19V16C20 15.4477 20.4477 15 21 15C21.5523 15 22 15.4477 22 16V19C22 19.7957 21.6837 20.5585 21.1211 21.1211C20.5585 21.6837 19.7957 22 19 22H16C15.4477 22 15 21.5523 15 21C15 20.4477 15.4477 20 16 20H19C19.2652 20 19.5195 19.8946 19.707 19.707C19.8946 19.5195 20 19.2652 20 19ZM2 8V5C2 4.20435 2.3163 3.44152 2.87891 2.87891C3.44152 2.3163 4.20435 2 5 2H8C8.55228 2 9 2.44772 9 3C9 3.55228 8.55228 4 8 4H5C4.73478 4 4.4805 4.10543 4.29297 4.29297C4.10543 4.4805 4 4.73478 4 5V8C4 8.55228 3.55228 9 3 9C2.44772 9 2 8.55228 2 8ZM20 8V5C20 4.73478 19.8946 4.48051 19.707 4.29297C19.5195 4.10543 19.2652 4 19 4H16C15.4477 4 15 3.55228 15 3C15 2.44772 15.4477 2 16 2H19C19.7957 2 20.5585 2.3163 21.1211 2.87891C21.6837 3.44151 22 4.20435 22 5V8C22 8.55228 21.5523 9 21 9C20.4477 9 20 8.55228 20 8Z"/>
      </svg>
    ),
    'share-2': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
      </svg>
    ),
    'download': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
    ),
    'refresh-cw': (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M0 20.0004V14.0004C0 13.4481 0.447715 13.0004 1 13.0004H7C7.55228 13.0004 8 13.4481 8 14.0004C7.99978 14.5525 7.55215 15.0004 7 15.0004H3.52539L6.3252 17.6313L6.34766 17.6528C7.30291 18.6085 8.48419 19.3075 9.78223 19.683C11.0804 20.0585 12.4534 20.0984 13.7715 19.8002C15.0894 19.5019 16.3104 18.8746 17.3203 17.977C18.3303 17.0793 19.0961 15.9403 19.5469 14.6664C19.7311 14.1459 20.3027 13.8731 20.8232 14.0571C21.3438 14.2413 21.6168 14.8128 21.4326 15.3334C20.8691 16.9259 19.911 18.3499 18.6484 19.4721C17.3859 20.5942 15.8594 21.3786 14.2119 21.7514C12.5645 22.124 10.8491 22.0732 9.22656 21.6039C7.61389 21.1374 6.1453 20.2718 4.95508 19.0883L2 16.311V20.0004C1.99978 20.5525 1.55215 21.0004 1 21.0004C0.447853 21.0004 0.000222588 20.5525 0 20.0004ZM9.78809 2.24846C11.4355 1.87581 13.1509 1.9266 14.7734 2.39592C16.3861 2.86243 17.8547 3.72809 19.0449 4.91154L22 7.68791V4.00041C22 3.44813 22.4477 3.00041 23 3.00041C23.5523 3.00041 24 3.44813 24 4.00041V10.0004C23.9998 10.5525 23.5521 11.0004 23 11.0004H17C16.4479 11.0004 16.0002 10.5525 16 10.0004C16 9.44813 16.4477 9.00041 17 9.00041H20.4756L17.6748 6.36857C17.6673 6.36151 17.6596 6.35438 17.6523 6.34709C16.6972 5.39146 15.5157 4.69331 14.2178 4.31779C12.9196 3.9423 11.5466 3.9014 10.2285 4.19963C8.91062 4.4979 7.68965 5.12521 6.67969 6.02287C5.66972 6.92056 4.90394 8.05959 4.45312 9.33342C4.26892 9.85395 3.69731 10.1268 3.17676 9.94279C2.65611 9.75855 2.38314 9.18707 2.56738 8.66643C3.1309 7.07397 4.08897 5.64996 5.35156 4.52775C6.61409 3.40571 8.14066 2.62121 9.78809 2.24846Z"/>
      </svg>
    ),
    'camera': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
        <circle cx="12" cy="13" r="4"/>
      </svg>
    )
  };

  const iconSvg = icons[name];

  if (!iconSvg) {
    console.warn(`Icon "${name}" not found. Available icons:`, Object.keys(icons));
    return null;
  }

  return (
    <span
      className={`inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
      {...props}
    >
      {React.cloneElement(iconSvg, {
        width: size,
        height: size,
        style: { width: size, height: size }
      })}
    </span>
  );
}

// Export convenience components for common icons
export const ArrowRightIcon = (props) => <Icon name="arrow-right" {...props} />;
export const ArrowLeftIcon = (props) => <Icon name="arrow-left" {...props} />;
export const UserIcon = (props) => <Icon name="user" {...props} />;
export const SettingsIcon = (props) => <Icon name="settings" {...props} />;
export const MenuIcon = (props) => <Icon name="menu" {...props} />;
export const CloseIcon = (props) => <Icon name="x" {...props} />;
export const SearchIcon = (props) => <Icon name="search" {...props} />;
export const BellIcon = (props) => <Icon name="bell" {...props} />;
export const MessageIcon = (props) => <Icon name="message-circle" {...props} />;
export const CalendarIcon = (props) => <Icon name="calendar" {...props} />;
export const HomeIcon = (props) => <Icon name="home" {...props} />;
export const StarIcon = (props) => <Icon name="star" {...props} />;
export const CheckIcon = (props) => <Icon name="check" {...props} />;
export const PlusIcon = (props) => <Icon name="plus" {...props} />;
export const EditIcon = (props) => <Icon name="edit" {...props} />;
export const TrashIcon = (props) => <Icon name="trash-2" {...props} />;